import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  FileText, Database, ImageIcon, Mail, Bot, Users as UsersIcon,
  TrendingUp, ArrowUpRight, Plus, Activity as ActivityIcon, Clock,
} from 'lucide-react';
import { api, unwrap } from '@/lib/api';
import { pickLocalized, type Localized } from '@/lib/i18n';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton, SkeletonRows } from '@/components/ui/Skeleton';

interface DashStats {
  pages: { total: number; published: number; drafts: number; archived: number };
  posts: { total: number; published: number; drafts: number };
  media: { total: number; total_size_mb: number };
  forms: { total: number; submissions_total: number; submissions_24h: number; submissions_7d: number };
  leads: { total: number; last_24h: number };
  users: { total: number };
}

interface ActivityItem {
  type: string;
  icon: string;
  title: Localized;
  meta: string;
  href: string;
  at: string;
}

export const Route = createFileRoute('/_app/')({
  component: Dashboard,
});

function Dashboard() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => unwrap<DashStats>(api.get('/dashboard/stats')),
    refetchInterval: 30_000,
  });

  const { data: activity, isLoading: activityLoading } = useQuery({
    queryKey: ['dashboard-activity'],
    queryFn: () => unwrap<ActivityItem[]>(api.get('/dashboard/activity')),
    refetchInterval: 30_000,
  });

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 md:p-8">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Escritorio</h1>
          <p className="text-sm text-neutral-500">
            Bienvenido. Aquí tienes una visión general del sitio.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/pages">Ver páginas</Link>
          </Button>
          <Button asChild>
            <Link to="/pages/$id" params={{ id: 'new' }}>
              <Plus className="size-4" /> Nueva página
            </Link>
          </Button>
        </div>
      </header>

      {/* KPI grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={FileText}
          label="Páginas"
          value={stats?.pages.total ?? 0}
          loading={isLoading}
          sub={stats ? `${stats.pages.published} publicadas · ${stats.pages.drafts} borradores` : '—'}
          accent="lime"
          href="/pages"
        />
        <StatCard
          icon={Database}
          label="Contenidos"
          value={stats?.posts.total ?? 0}
          loading={isLoading}
          sub={stats ? `${stats.posts.published} publicados · ${stats.posts.drafts} borradores` : '—'}
          accent="blue"
          href="/post-types"
        />
        <StatCard
          icon={ImageIcon}
          label="Multimedia"
          value={stats?.media.total ?? 0}
          loading={isLoading}
          sub={stats ? `${stats.media.total_size_mb.toFixed(1)} MB en uso` : '—'}
          accent="purple"
          href="/media"
        />
        <StatCard
          icon={Mail}
          label="Envíos · 24h"
          value={stats?.forms.submissions_24h ?? 0}
          loading={isLoading}
          sub={stats ? `${stats.forms.submissions_total} totales · ${stats.forms.total} forms` : '—'}
          accent="emerald"
          href="/forms"
          trend={stats ? stats.forms.submissions_7d : null}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Activity */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base">Actividad reciente</CardTitle>
              <CardDescription>Últimas ediciones y envíos.</CardDescription>
            </div>
            <ActivityIcon className="size-4 text-neutral-400" />
          </CardHeader>
          <CardContent>
            {activityLoading && <SkeletonRows rows={5} className="py-1" />}
            {!activityLoading && (!activity || activity.length === 0) && (
              <p className="py-8 text-center text-sm text-neutral-400">Sin actividad reciente.</p>
            )}
            <ul className="divide-y divide-neutral-100">
              {activity?.map((item, idx) => (
                <li key={`${item.type}-${idx}`}>
                  <Link
                    to={item.href}
                    className="group -mx-2 flex items-start justify-between gap-3 rounded-md px-2 py-3 transition-colors hover:bg-neutral-50"
                  >
                    <div className="flex items-start gap-3">
                      <span className={`grid size-9 place-items-center rounded-md ${badgeBg(item.type)}`}>
                        {iconFor(item.type)}
                      </span>
                      <div>
                        <div className="text-sm font-medium leading-tight group-hover:underline">{pickLocalized(item.title)}</div>
                        <div className="text-xs text-neutral-500">{item.meta}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 whitespace-nowrap text-xs text-neutral-400">
                      <Clock className="size-3" />
                      {timeAgo(item.at)}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Quick actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Accesos rápidos</CardTitle>
            <CardDescription>Lo que sueles hacer.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <QuickLink to="/pages/$id" params={{ id: 'new' }} icon={Plus} label="Crear nueva página" />
            <QuickLink to="/media" icon={ImageIcon} label="Subir archivos" />
            <QuickLink to="/forms" icon={Mail} label="Revisar envíos" />
            <QuickLink to="/ai/leads" icon={Bot} label="Leads del chatbot" />
            <QuickLink to="/users" icon={UsersIcon} label="Gestionar usuarios" />
          </CardContent>
        </Card>
      </div>

      {/* Secondary metrics */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <MiniCard
          icon={Bot}
          label="Leads totales (chatbot IA)"
          value={stats?.leads.total ?? 0}
          sub={stats ? `${stats.leads.last_24h} en las últimas 24h` : '—'}
        />
        <MiniCard
          icon={UsersIcon}
          label="Usuarios del CMS"
          value={stats?.users.total ?? 0}
          sub="Administradores y editores"
        />
        <MiniCard
          icon={TrendingUp}
          label="Envíos esta semana"
          value={stats?.forms.submissions_7d ?? 0}
          sub="Suma de todos los formularios"
        />
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon, label, value, sub, loading, accent, href, trend,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  sub: string;
  loading?: boolean;
  accent: 'lime' | 'blue' | 'purple' | 'emerald';
  href: string;
  trend?: number | null;
}) {
  const accents = {
    lime: 'from-lime-50 to-lime-100 text-lime-900 border-lime-200 dark:from-lime-500/10 dark:to-lime-500/5 dark:text-lime-200 dark:border-lime-500/20',
    blue: 'from-blue-50 to-blue-100 text-blue-900 border-blue-200 dark:from-blue-500/10 dark:to-blue-500/5 dark:text-blue-200 dark:border-blue-500/20',
    purple: 'from-purple-50 to-purple-100 text-purple-900 border-purple-200 dark:from-purple-500/10 dark:to-purple-500/5 dark:text-purple-200 dark:border-purple-500/20',
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-900 border-emerald-200 dark:from-emerald-500/10 dark:to-emerald-500/5 dark:text-emerald-200 dark:border-emerald-500/20',
  };
  return (
    <Link to={href} className="group block">
      <Card className="overflow-hidden transition-shadow hover:shadow-md">
        <div className={`bg-gradient-to-br ${accents[accent]} border-b p-4`}>
          <div className="flex items-start justify-between">
            <span className="grid size-9 place-items-center rounded-lg bg-white/80 shadow-sm dark:bg-white/10">
              <Icon className="size-4" />
            </span>
            <ArrowUpRight className="size-4 opacity-50 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
          <div className="mt-3">
            {loading ? (
              <Skeleton className="h-9 w-20 bg-black/10 dark:bg-white/10" />
            ) : (
              <div className="text-3xl font-bold tabular-nums">{value.toLocaleString('es')}</div>
            )}
            <div className="text-xs font-semibold uppercase tracking-wider opacity-70">{label}</div>
          </div>
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 text-xs text-neutral-500">
          {loading ? <Skeleton className="h-3 w-32" /> : <span>{sub}</span>}
          {trend != null && (
            <Badge variant="success" className="text-[10px]">
              {trend}/7d
            </Badge>
          )}
        </div>
      </Card>
    </Link>
  );
}

function MiniCard({
  icon: Icon, label, value, sub,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  sub: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <span className="grid size-10 place-items-center rounded-lg bg-neutral-100">
          <Icon className="size-5 text-neutral-700" />
        </span>
        <div>
          <div className="text-2xl font-bold tabular-nums">{value.toLocaleString('es')}</div>
          <div className="text-xs font-medium text-neutral-700">{label}</div>
          <div className="text-[11px] text-neutral-400">{sub}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickLink({
  to, params, icon: Icon, label,
}: {
  to: string;
  params?: Record<string, string>;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <Link
      to={to}
      params={params}
      className="flex items-center justify-between rounded-md border border-neutral-200 px-3 py-2 text-sm transition-colors hover:border-neutral-400 hover:bg-neutral-50"
    >
      <span className="flex items-center gap-2">
        <Icon className="size-4 text-neutral-500" />
        {label}
      </span>
      <ArrowUpRight className="size-3.5 text-neutral-400" />
    </Link>
  );
}

function iconFor(type: string) {
  if (type === 'page') return <FileText className="size-4 text-neutral-700" />;
  if (type === 'post') return <Database className="size-4 text-neutral-700" />;
  if (type === 'submission') return <Mail className="size-4 text-neutral-700" />;
  return <ActivityIcon className="size-4 text-neutral-700" />;
}

function badgeBg(type: string) {
  if (type === 'page') return 'bg-lime-100';
  if (type === 'post') return 'bg-blue-100';
  if (type === 'submission') return 'bg-emerald-100';
  return 'bg-neutral-100';
}

function timeAgo(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `hace ${days}d`;
  return d.toLocaleDateString('es', { day: 'numeric', month: 'short' });
}
